/**
 * @file components/SignaturePad.js
 * @description Professional canvas-based signature capture component.
 * Supports touch (primary use in field), mouse, and stylus with pressure sensitivity.
 * Uses quadratic bezier curves for smooth line rendering.
 * Captures signature as base64 PNG data URL.
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, Input } from '../design-system';

/**
 * SignaturePad
 * @param {object} props
 * @param {string} [props.label] - Label shown above the pad
 * @param {string} [props.value] - Existing signature as base64 data URL (for display)
 * @param {function} props.onChange - Called with { signatureData, signedByName, signedAt }
 * @param {boolean} [props.readOnly] - Show signature without edit capability
 * @param {string} [props.signedByName] - Pre-filled typed name
 * @param {string} [props.signedAt] - Timestamp when signed
 * @param {string} [props.penColor] - 'black' (default) or 'blue'
 * @param {number} [props.penWidth] - Base pen width in px (default 2)
 */
function SignaturePad({
  label = 'Signature',
  value,
  onChange,
  readOnly = false,
  signedByName: initialName = '',
  signedAt,
  penColor = 'black',
  penWidth = 2
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [typedName, setTypedName] = useState(initialName);
  const PEN_COLORS = { black: '#1d1d1f', blue: '#1a3f7a' };
  const [activePenColor, setActivePenColor] = useState(PEN_COLORS[penColor] || PEN_COLORS.black);
  const pointsRef = useRef([]);
  const strokesRef = useRef([]);
  const lastPointRef = useRef(null);

  const drawSmoothLine = useCallback((ctx, points, color, baseWidth) => {
    if (points.length < 2) return;
    ctx.strokeStyle = color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (points.length === 2) {
      const w = baseWidth * (points[0].pressure + points[1].pressure) / 2;
      ctx.lineWidth = Math.max(0.5, w);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      ctx.lineTo(points[1].x, points[1].y);
      ctx.stroke();
      return;
    }

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const lw = baseWidth * (p0.pressure + p1.pressure) / 2;
      ctx.lineWidth = Math.max(0.5, lw);
      ctx.beginPath();

      if (i === 0) {
        ctx.moveTo(p0.x, p0.y);
      } else {
        const prev = points[i - 1];
        ctx.moveTo((prev.x + p0.x) / 2, (prev.y + p0.y) / 2);
      }

      if (i === points.length - 2) {
        ctx.lineTo(p1.x, p1.y);
      } else {
        ctx.quadraticCurveTo(p0.x, p0.y, (p0.x + p1.x) / 2, (p0.y + p1.y) / 2);
      }
      ctx.stroke();
    }
  }, []);

  const redrawAllStrokes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    strokesRef.current.forEach((stroke) => {
      drawSmoothLine(ctx, stroke.points, stroke.color, stroke.width);
    });
    setIsEmpty(strokesRef.current.length === 0);
  }, [drawSmoothLine]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = rect.width;
    const height = 200;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    redrawAllStrokes();
  }, [redrawAllStrokes]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  useEffect(() => {
    if (value && canvasRef.current && strokesRef.current.length === 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.restore();
        setIsEmpty(false);
      };
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY, pressure;
    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      pressure = e.touches[0].force || 0.5;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
      pressure = e.pressure || 0.5;
    }
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      pressure: Math.max(0.1, Math.min(1, pressure))
    };
  };

  const emitChange = () => {
    if (!onChange) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (strokesRef.current.length === 0) {
      onChange({ signatureData: '', signedByName: typedName, signedAt: null });
      return;
    }
    onChange({
      signatureData: canvas.toDataURL('image/png'),
      signedByName: typedName,
      signedAt: new Date().toISOString()
    });
  };

  const startDrawing = (e) => {
    if (readOnly) return;
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    pointsRef.current = [pos];
    lastPointRef.current = pos;
  };

  const draw = (e) => {
    if (!isDrawing || readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    const last = lastPointRef.current;
    const dx = pos.x - last.x;
    const dy = pos.y - last.y;
    if (dx * dx + dy * dy < 4) return;
    pointsRef.current.push(pos);
    lastPointRef.current = pos;
    const pts = pointsRef.current;
    const len = pts.length;
    if (len >= 3) {
      drawSmoothLine(ctx, pts.slice(len - 3), activePenColor, penWidth);
    } else if (len === 2) {
      drawSmoothLine(ctx, pts, activePenColor, penWidth);
    }
    setIsEmpty(false);
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    if (pointsRef.current.length > 1) {
      strokesRef.current.push({
        points: pointsRef.current.slice(),
        color: activePenColor,
        width: penWidth
      });
      redrawAllStrokes();
    }
    pointsRef.current = [];
    lastPointRef.current = null;
    emitChange();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    if (onChange) {
      const canvas = canvasRef.current;
      const dataUrl = (canvas && strokesRef.current.length > 0) ? canvas.toDataURL('image/png') : '';
      onChange({
        signatureData: dataUrl,
        signedByName: name,
        signedAt: dataUrl ? new Date().toISOString() : null
      });
    }
  };

  const handleClear = () => {
    strokesRef.current = [];
    pointsRef.current = [];
    redrawAllStrokes();
    setIsEmpty(true);
    if (onChange) {
      onChange({ signatureData: '', signedByName: typedName, signedAt: null });
    }
  };

  const handleUndo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    redrawAllStrokes();
    emitChange();
  };

  // Read-only display
  if (readOnly && value) {
    return (
      <div style={styles.container}>
        {label && <div style={styles.label}>{label}</div>}
        <div style={styles.readOnlyWrapper}>
          <img src={value} alt={label} style={styles.readOnlyImg} />
        </div>
        {initialName && <div style={styles.readOnlyName}>{initialName}</div>}
        {signedAt && (
          <div style={styles.readOnlyDate}>
            Signed: {new Date(signedAt).toLocaleString()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {label && <div style={styles.label}>{label}</div>}
      <div ref={containerRef} style={styles.canvasWrapper}>
        <canvas
          ref={canvasRef}
          style={{
            ...styles.canvas,
            cursor: readOnly ? 'default' : 'crosshair',
            touchAction: 'none'
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {isEmpty && !readOnly && (
          <div style={styles.placeholder}>
            <div style={styles.placeholderLine} />
            <span style={styles.placeholderText}>Sign here</span>
          </div>
        )}
      </div>

      {!readOnly && (
        <>
          <div style={styles.toolbar}>
            <div style={styles.toolbarLeft}>
              <Button variant="ghost" size="sm" onClick={handleClear} style={styles.toolButton}>
                Clear
              </Button>
              <Button variant="ghost" size="sm" onClick={handleUndo} style={styles.toolButton}>
                Undo
              </Button>
            </div>
            <div style={styles.toolbarRight}>
              <button
                type="button"
                onClick={() => setActivePenColor(PEN_COLORS.black)}
                style={{
                  ...styles.colorDot,
                  backgroundColor: PEN_COLORS.black,
                  outline: activePenColor === PEN_COLORS.black ? '2px solid var(--color-primary, #0066cc)' : 'none',
                  outlineOffset: '2px'
                }}
                title="Black pen"
                aria-label="Black pen"
              />
              <button
                type="button"
                onClick={() => setActivePenColor(PEN_COLORS.blue)}
                style={{
                  ...styles.colorDot,
                  backgroundColor: PEN_COLORS.blue,
                  outline: activePenColor === PEN_COLORS.blue ? '2px solid var(--color-primary, #0066cc)' : 'none',
                  outlineOffset: '2px'
                }}
                title="Blue pen"
                aria-label="Blue pen"
              />
              {!isEmpty && <span style={styles.signed}>Signed</span>}
            </div>
          </div>
          <div style={styles.nameRow}>
            <Input
              label="Print full name"
              value={typedName}
              onChange={handleNameChange}
              placeholder="Type your full name"
            />
          </div>
        </>
      )}
    </div>
  );
}

SignaturePad.validate = function(sigData) {
  if (!sigData) return 'Signature is required';
  if (!sigData.signatureData || !sigData.signatureData.startsWith('data:image/')) {
    return 'Please draw your signature';
  }
  if (!sigData.signedByName || sigData.signedByName.trim().length === 0) {
    return 'Please type your name';
  }
  return null;
};

const styles = {
  container: { marginBottom: '20px' },
  label: {
    fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary, #6e6e73)',
    marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em',
    fontFamily: 'var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'
  },
  canvasWrapper: {
    position: 'relative', border: '2px dashed var(--border-default, #d2d2d7)',
    borderRadius: '12px', overflow: 'hidden', backgroundColor: '#ffffff', minHeight: '200px'
  },
  canvas: { display: 'block', width: '100%', height: '200px', backgroundColor: '#ffffff' },
  placeholder: {
    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
  },
  placeholderLine: { width: '70%', height: '1px', borderBottom: '1px dashed #c7c7cc', marginBottom: '8px' },
  placeholderText: {
    color: '#c7c7cc', fontSize: '15px', fontStyle: 'italic',
    fontFamily: 'var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'
  },
  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', gap: '8px' },
  toolbarLeft: { display: 'flex', gap: '4px' },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  toolButton: { minHeight: '48px', minWidth: '48px' },
  colorDot: {
    width: '48px', height: '48px', borderRadius: '50%', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '10px', backgroundClip: 'content-box'
  },
  signed: {
    fontSize: '13px', color: 'var(--color-success, #34c759)', fontWeight: '600',
    fontFamily: 'var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'
  },
  nameRow: { marginTop: '12px' },
  readOnlyWrapper: {
    border: '1px solid var(--border-default, #d2d2d7)', borderRadius: '12px',
    overflow: 'hidden', backgroundColor: '#ffffff', padding: '8px'
  },
  readOnlyImg: { display: 'block', width: '100%', maxHeight: '200px', objectFit: 'contain' },
  readOnlyName: {
    fontSize: '14px', fontWeight: '600', color: 'var(--text-primary, #1d1d1f)',
    marginTop: '6px', fontFamily: 'var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'
  },
  readOnlyDate: {
    fontSize: '12px', color: 'var(--text-tertiary, #86868b)', marginTop: '2px',
    fontFamily: 'var(--font-family, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'
  }
};

export default SignaturePad;
